import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Spin } from './Button.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';

/**
 * O gatilho compacto do `SearchSelect` e do `MultiSelect` (07/10/2026): uma
 * pastilha de ícone no lugar do campo, para escolher sem ocupar uma linha —
 * o analista e o vencimento abaixo do nome na mesa, a etiqueta no alto do miolo.
 *
 * - `filled`: a pastilha cheia, com as cores do `PersonMark` — há escolha.
 * - vazia: tracejada — ainda não há; o ícone diz o que ela põe (a pessoa, o
 *   calendário, a etiqueta), para as pastilhas lado a lado não serem iguais.
 *
 * O texto vai no `title` e no nome acessível, e nunca escrito ao lado: é o que
 * deixa um nome comprido caber sem quebrar o cabeçalho. Quem passa o ponteiro
 * lê; quem usa leitor de tela ouve.
 */
export interface CompactTrigger {
  icon: IconName;
  /** O que a pastilha diz — "Analista responsável: Fulano", "Adicionar etiqueta". */
  title: string;
  filled?: boolean;
  /** Gravando a escolha: o giro no lugar do ícone, e a pastilha não abre. */
  loading?: boolean;
  /**
   * O texto escrito ao lado da pastilha — o nome do analista escolhido. Corta
   * com reticências numa largura máxima, e o `title` diz inteiro.
   */
  text?: string;
  /**
   * A situação que a pastilha sinaliza sem ser `filled`: há valor e está em
   * dia (`neutro`), perto (`alerta`) ou passado (`erro`) — o vencimento do
   * card. Sem `tone` nem `filled`, a pastilha é tracejada: ainda não há valor.
   */
  tone?: 'neutro' | 'alerta' | 'erro';
}

export const CompactTriggerButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & { compact: CompactTrigger }
>(function CompactTriggerButton({ compact, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      title={compact.title}
      aria-label={compact.title}
      {...props}
      disabled={props.disabled || compact.loading}
      aria-busy={compact.loading || undefined}
      className={cn(
        'group inline-flex min-w-0 max-w-56 items-center gap-2 rounded-pequeno text-left',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-pequeno transition-colors pointer-coarse:size-11',
          compact.filled
            ? 'bg-chrome-alto text-chrome-texto group-hover:opacity-90 group-active:opacity-80'
            : compact.tone === 'erro'
              ? 'bg-erro-suave text-erro group-hover:opacity-90'
              : compact.tone === 'alerta'
                ? 'bg-alerta-suave text-alerta group-hover:opacity-90'
                : compact.tone === 'neutro'
                  ? 'bg-superficie-suave text-texto-suave group-hover:text-texto'
                  : 'border border-dashed border-borda-forte text-texto-suave group-hover:border-texto-suave group-hover:text-texto group-active:bg-superficie-suave',
        )}
      >
        {compact.loading ? <Spin size={16} /> : <Icon nome={compact.icon} size={16} />}
      </span>
      {compact.text && (
        <span className="truncate text-xs font-medium text-texto group-hover:underline">
          {compact.text}
        </span>
      )}
    </button>
  );
});
